import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { businessNeeds, journeySteps, mobileModules, systemModules, type SystemModule } from '../../data/heroBlueprint';
const moduleBar = {
  orange: 'bg-forge-orange',
  cyan: 'bg-forge-cyan',
  green: 'bg-forge-green'
} as const;
type Geometry = {
  width: number;
  height: number;
  paths: string[];
  target: {
    x: number;
    y: number;
  } | null;
};
const EMPTY: Geometry = {
  width: 0,
  height: 0,
  paths: [],
  target: null
};

/**
 * Hero object: what the business needs on the left, the WebsiteForge platform and team
 * in the center. Connector routes are measured from the live layout so they always align.
 * Below 640px the need cards collapse to a single journey row and the modules condense to three.
 */
export function HeroBlueprint() {
  const stageRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const needRefs = useRef<Array<HTMLLIElement | null>>([]);
  const [geo, setGeo] = useState<Geometry>(EMPTY);
  const measure = useCallback(() => {
    const stage = stageRef.current;
    const panel = panelRef.current;
    if (!stage || !panel) return;
    if (window.innerWidth < 1024) {
      setGeo(EMPTY);
      return;
    }
    const base = stage.getBoundingClientRect();
    const target = panel.getBoundingClientRect();
    const tx = target.left - base.left - 1;
    const ty = target.top - base.top + target.height / 2;
    const paths = needRefs.current.filter((el): el is HTMLLIElement => Boolean(el)).map(el => {
      const r = el.getBoundingClientRect();
      const x = r.right - base.left;
      const y = r.top - base.top + r.height / 2;
      const dx = Math.max((tx - x) * 0.55, 18);
      return `M ${x} ${y} C ${x + dx} ${y}, ${tx - dx} ${ty}, ${tx} ${ty}`;
    });
    setGeo({
      width: base.width,
      height: base.height,
      paths,
      target: {
        x: tx,
        y: ty
      }
    });
  }, []);
  useLayoutEffect(() => {
    measure();
  }, [measure]);
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(stage);
    window.addEventListener('resize', measure);
    const t = window.setTimeout(measure, 400);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.clearTimeout(t);
    };
  }, [measure]);
  return <div className="relative" data-id="element-147" data-mp="3q7r5tf3q4egl">
      <div className="pointer-events-none absolute -inset-6 rounded-[44px] bg-forge-cyan/[0.06] blur-3xl" aria-hidden="true" data-id="element-148" data-mp="3q4bpk8ybczdl" />

      <div className="relative overflow-hidden rounded-[24px] border-2 border-white/14 bg-navy-950/90 shadow-lift" data-id="element-149" data-mp="3zhugmqnab9p">
        <div className="absolute inset-0 bp opacity-80" aria-hidden="true" data-id="element-150" data-mp="8q6kdxmof06q" />

        <div className="relative flex items-center gap-3 border-b border-white/10 px-5 py-4 sm:px-6 sm:py-5" data-id="element-151" data-mp="37fyfxtjyb9d3">
          <span className="h-3 w-3 rotate-45 bg-forge-orange" aria-hidden="true" data-id="element-152" data-mp="3cehzjnb2o92a" />
          <h2 className="font-display text-[15px] font-extrabold leading-tight tracking-[-0.01em] text-white sm:text-[17px]" data-id="element-153" data-mp="1tjwyl6u86tmu">
            One system, built around the operation
          </h2>
        </div>

        {/* compact journey — small screens only */}
        <div className="relative border-b border-white/10 px-5 py-4 sm:hidden" data-id="element-154" data-mp="1khnrys22vkeq">
          <ol className="flex flex-wrap items-center gap-x-2 gap-y-1.5" data-id="element-155" data-mp="1fj2s4tqqn2qt">
            {journeySteps.map((step, i) => <li key={step} className="flex items-center gap-2" data-id="element-156" data-mp="1amm4s1rd4ses" data-mpi={i}>
                <span className="text-[13px] font-semibold text-mist-100" data-id="element-157" data-mp="15u2m4qdtj734">{step}</span>
                {i !== journeySteps.length - 1 && <span className="text-forge-orange" aria-hidden="true" data-id="element-158" data-mp="11szcwmqa09av">
                    →
                  </span>}
              </li>)}
          </ol>
        </div>

        <div ref={stageRef} className="relative grid min-w-0 items-center gap-0 px-5 py-5 sm:px-7 sm:py-7 lg:grid-cols-[0.78fr_4rem_1.55fr]" data-id="element-159" data-mp="rtrzy0ngegoa">
          {geo.paths.length > 0 && <svg className="pointer-events-none absolute inset-0 z-0 hidden h-full w-full lg:block" viewBox={`0 0 ${geo.width} ${geo.height}`} width={geo.width} height={geo.height} aria-hidden="true" data-id="element-160" data-mp="2n02czcazvsv7">
              {geo.paths.map(d => <path key={d} d={d} fill="none" stroke="#6E79E6" strokeWidth="1.6" strokeLinecap="round" opacity="0.6" className="flowline" data-id="element-161" data-mp="247hyizw1lqax" />)}
              {geo.target && <>
                  <circle cx={geo.target.x} cy={geo.target.y} r="7" fill="#F5821F" opacity="0.22" data-id="element-162" data-mp="1za8415tsqog8" />
                  <circle cx={geo.target.x} cy={geo.target.y} r="3.5" fill="#F5821F" data-id="element-163" data-mp="3ol8uou36ooa3" />
                </>}
            </svg>}

          {/* what the business needs — sm and up */}
          <motion.div initial={{
          opacity: 0,
          x: -14
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5,
          delay: 0.08
        }} className="relative z-10 hidden min-w-0 sm:block" data-id="element-164" data-mp="1hwgkboldbvye">
            <p className="flex items-center gap-2 font-mono text-[11.5px] uppercase tracking-[0.14em] text-forge-cyan" data-id="element-165" data-mp="1mxmdtomurn8z">
              <span className="h-px w-4 bg-current opacity-60" aria-hidden="true" data-id="element-166" data-mp="20nzqu5elps56" />
              What the business needs
            </p>
            <ul className="mt-3.5 grid grid-cols-2 gap-2 lg:grid-cols-1" data-id="element-167" data-mp="1wq1vsly5gbtv">
              {businessNeeds.map((need, i) => <li key={need} ref={el => {
              needRefs.current[i] = el;
            }} className="rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-3 text-[13.5px] font-medium leading-snug text-mist-100" data-id="element-168" data-mp="25nvta07nhbim" data-mpi={i}>
                  {need}
                </li>)}
            </ul>
          </motion.div>

          {/* connector column — desktop only */}
          <div className="relative z-10 hidden lg:block" aria-hidden="true" data-id="element-169" data-mp="2gdt7nwg0o0f0" />

          {/* platform + team */}
          <motion.div ref={panelRef} initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.55,
          delay: 0.2
        }} className="relative z-10 mt-5 min-w-0 rounded-2xl border-2 border-forge-orange/45 bg-navy-900 p-5 shadow-[0_0_70px_-18px_rgba(245,130,31,0.6)] sm:mt-0 sm:p-6" data-id="element-170" data-mp="2kfn1bkrso45o">
            <span className="absolute inset-x-0 top-0 h-[4px] rounded-t-2xl bg-forge-orange" aria-hidden="true" data-id="element-171" data-mp="343e0r85vwe20" />

            <p className="font-mono text-[11.5px] uppercase tracking-[0.14em] text-forge-orange" data-id="element-172" data-mp="3d1zij13j7p1q">
              WebsiteForge platform + team
            </p>
            <h3 className="mt-2 font-display text-[19px] font-extrabold leading-tight tracking-[-0.01em] text-white sm:text-[23px]" data-id="element-173" data-mp="3i1kh3aeshvdx">
              Designed and managed as one
            </h3>

            {/* three condensed modules on phones */}
            <ul className="mt-4 grid gap-2.5 sm:hidden" data-id="element-174" data-mp="3i6tg36mbszpv">
              {mobileModules.map(m => <ModuleRow key={m.label} module={m} data-id="element-175" data-mp="3e5hpoyvmoagm" />)}
            </ul>

            {/* full module list from sm up */}
            <ul className="mt-5 hidden gap-2.5 sm:grid sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2" data-id="element-176" data-mp="ep1kzjcw7zng">
              {systemModules.map((m, i) => <motion.li key={m.label} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.32 + i * 0.06
            }} className="flex min-w-0 items-stretch gap-3 rounded-xl bg-white/[0.055] px-4 py-3.5 ring-1 ring-inset ring-white/[0.09]" data-id="element-177" data-mp="9tckndt1ueq3" data-mpi={i}>
                  <span className={`w-[3px] shrink-0 rounded-full ${moduleBar[m.accent]}`} aria-hidden="true" data-id="element-178" data-mp="snrkt6r3kdfi" />
                  <span className="min-w-0" data-id="element-179" data-mp="1h4mkuha6j0mu">
                    <span className="block text-[14px] font-bold leading-snug text-white" data-id="element-180" data-mp="1h5o6ffotn5it">{m.label}</span>
                    <span className="mt-1 block text-[12.5px] leading-snug text-mist-300" data-id="element-181" data-mp="12h1hcm9e6zee">{m.note}</span>
                  </span>
                </motion.li>)}
            </ul>

            <p className="mt-4 border-t border-white/10 pt-3.5 text-[12.5px] font-medium text-mist-300" data-id="element-182" data-mp="1v5fkfgfc8lwt">
              One coordinated build. One accountable team.
            </p>
          </motion.div>
        </div>
      </div>
    </div>;
}
function ModuleRow({
  module: m
}: {
  module: SystemModule;
}) {
  return <li className="flex min-w-0 items-stretch gap-3 rounded-xl bg-white/[0.055] px-4 py-3 ring-1 ring-inset ring-white/[0.09]" data-id="element-183" data-mp="1q3q4pnh67k2e">
      <span className={`w-[3px] shrink-0 rounded-full ${moduleBar[m.accent]}`} aria-hidden="true" data-id="element-184" data-mp="9te2lnusz2zo" />
      <span className="min-w-0" data-id="element-185" data-mp="enrqz6b1s9uu">
        <span className="block text-[13.5px] font-bold leading-snug text-white" data-id="element-186" data-mp="2sxdqw20civ20">{m.label}</span>
        <span className="mt-1 block text-[12.5px] leading-snug text-mist-300" data-id="element-187" data-mp="2nzc0ew0lbtgi">{m.note}</span>
      </span>
    </li>;
}